import { HierarchyNode } from 'd3';
import { darken } from 'polished';
import { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';

type SRSHierarchyNode = {
	// TODO: add other properties
	key: string;
};

type ColorsByKey = { [key in string]: string };

// create color map
const rootColors = [
	'#a11aa1',
	'#6e3af2',
	'#0b53e3',
	'#006aff',
	'#00a68d',
	'#009c12',
	'#79b500',
	'#d48a00',
	'#d96900',
	'#8a4c19',
];

// These were randomly generated in range [0, -.2]
const brightnessModifiers = [
	-0.19,
	-0.06,
	-0.24,
	-0.09,
	-0.18,
	-0.25,
	-0.07,
	-0.02,
	-0.12,
	-0.13,
	-0.09,
	-0.13,
];

// Recursively generates colors for the tree that slowly
function generateChildrenColors(
	colors: ColorsByKey,
	color: string,
	node: HierarchyNode<SRSHierarchyNode>,
) {
	node.children?.forEach((child, j) => {
		const rawModifier = brightnessModifiers[j % brightnessModifiers.length];
		const modifier = Math.abs(rawModifier);

		// only generate a color if one isn't defined
		if (!colors[child.data.key]) {
			colors[child.data.key] = darken(modifier, color);
		}

		if (child.children) {
			// pass a less darkened value down so that the overall color darkens more slowly
			generateChildrenColors(colors, darken(modifier * 0.3, color), child);
		}
	});
}

// Keep rotating through colors so that colors are not repeated
// NOTE: might want to track this in react so that it persists in the component
// instead of in the webpage.
let rootColorOffset = -1;

function getRootNodeColor() {
	rootColorOffset = (rootColorOffset + 1) % rootColors.length;
	return rootColors[rootColorOffset];
}

// Creates a color map that aims to consistently color nodes even if they've
// been filtered out and back in again.
export function useColorHierarchy(
	hierarchy: HierarchyNode<SRSHierarchyNode>[],
): ColorsByKey {
	const [colorsByKey, setColorsByKey] = useState<ColorsByKey>({});

	// Update the color list when initially or when new nodes are added
	useEffect(() => {
		setColorsByKey((stateColors) => {
			const colors: ColorsByKey = cloneDeep(stateColors);

			hierarchy.forEach((node) => {
				const color = colors[node.data.key] ?? getRootNodeColor();
				colors[node.data.key] = color;
				if (node.children) {
					generateChildrenColors(colors, color, node);
				}
			});
			return colors;
		});
	}, [hierarchy]);

	return colorsByKey;
}
